import { gql } from "@apollo/client"
import { clone } from "lib/clone"
import noop from "lib/noop"
import useAsync from "lib/@hooks/useAsync"
import { indexTasksInSchedule } from "routes/schedule/lib/index-tasks-in-schedule"
import { mutate } from "lib/graphql/mutate"
import { query } from "lib/graphql/query"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { stillEditing } from "minimals-template/components/lock/controller/still-editing"
import { ensureTaskIds } from "routes/schedule/edit/ensure-task-ids"
import { NO_ACCESS_CODE, NOT_FOUND_CODE } from "routes/schedule/edit/schedule-edit-errors.mjs"

const PERMANENT_FAILURE_CODES = [NOT_FOUND_CODE, NO_ACCESS_CODE]

const methods = {
    draft,
    schedule,
    history,
    scheduleHistory,
    audit,
}

export const Methods = {
    draft: "draft",
    schedule: "schedule",
    draftHistory: "history",
    scheduleHistory: "scheduleHistory",
    audit: "audit",
}

async function draft(id) {
    const { definition } = await query(
        gql`
            query getDraftSchedule($id: String!) {
                definition: editSchedule(id: $id)
            }
        `,
        { id },
        { fetchPolicy: "network-only" }
    )
    if (!definition) return null
    return [
        indexTasksInSchedule({ ...clone(definition), $name: `Draft of ${definition.title}` }),
        async () => {
            await stillEditing(id)
        },
        async (content) => {
            await mutate(
                gql`
                    mutation write($id: String!, $content: JSONObject!) {
                        writeContent(content: $content, id: $id, unlock: true)
                    }
                `,
                { id, content },
                { fetchPolicy: "network-only" }
            )
        },
        `Draft of ${definition.title}`,
    ]
}

async function schedule(id) {
    const { definition } = await query(
        gql`
            query rawSchedule($id: String!) {
                definition: rawSchedule(id: $id)
            }
        `,
        { id }
    )
    if (!definition) return null
    return [
        indexTasksInSchedule(
            clone({
                ...definition,
                $name: `Published version of ${definition.title}`,
            })
        ),
        noop,
        noop,
        `Published version of ${definition.title}`,
    ]
}

async function history(id) {
    const { definition } = await query(
        gql`
            query getHistory($id: String!) {
                definition: draftHistoryEntry(entryId: $id)
            }
        `,
        { id }
    )
    if (!definition?.draft) return null
    return [
        indexTasksInSchedule(
            clone({
                ...definition.draft,
                $name: `Draft of ${definition.draft.title} as of ${new Date(definition.updated).long()}`,
            })
        ),
        noop,
        noop,
        `Draft of ${definition.draft.title} as of ${new Date(definition.updated).long()}`,
    ]
}

async function scheduleHistory(id) {
    const { definition } = await query(
        gql`
            query getScheduleHistoryRecord($id: String!) {
                definition: getScheduleHistoryRecord(id: $id)
            }
        `,
        { id }
    )
    if (!definition) return null
    return [
        indexTasksInSchedule(
            clone({
                ...definition,
                $name: `Version: ${definition.version}`,
            })
        ),
        noop,
        noop,
        `Version: ${definition.version}`,
    ]
}

async function audit(id) {
    const { getAudit } = await query(
        gql`
            query GetAudit($id: String!) {
                getAudit(id: $id) {
                    payload
                }
            }
        `,
        { id }
    )
    const definition = getAudit?.payload?.content
    if (!definition) return null
    return [
        indexTasksInSchedule(
            clone({
                ...definition,
                $name: `Version: ${definition.version}`,
            })
        ),
        noop,
        noop,
        `Proposed Version: ${+definition.version + 1}`,
    ]
}

export function useScheduleByMethodId(idWithMethod, refId = "") {
    let method, id
    if (idWithMethod) {
        ;[method, id] = idWithMethod.split("!")
    }
    // compare pages need loading told apart from a missing (null) schedule
    return useScheduleByMethod(method, id, refId, IS_LOADING)
}

export function useScheduleByMethod(method, id, refId = "", initialValue = null) {
    const fn = Object.hasOwn(methods, method) ? methods[method] : () => null
    return ensureTaskIds(useAsync(() => loadSchedule(fn, id), initialValue, [method, id, refId]))

    async function loadSchedule(fn, id) {
        try {
            return await fn(id)
        } catch (error) {
            // missing or no access -> will never load, treat as missing (codes survive production error masking)
            const codes = error?.graphQLErrors?.map((graphQLError) => graphQLError.extensions?.code) ?? []
            if (codes.some((code) => PERMANENT_FAILURE_CODES.includes(code))) return null
            throw error
        }
    }
}
